import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Search, FileText, FileSpreadsheet, FileImage, Download, Pencil, Trash2, MoreHorizontal, Folder, Link2 } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { DocumentEditDialog } from "@/components/run/DocumentEditDialog";
import { runService } from "@/services/runService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { fmtDate } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const ALL = "__all__";
const fmtSize = (b) => (b == null ? "—" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const fileIcon = (t) => (["xls", "xlsx"].includes(t) ? FileSpreadsheet : ["jpg", "jpeg", "png"].includes(t) ? FileImage : FileText);

export default function Documents() {
  const navigate = useNavigate();
  const { openUploadDocument } = useCrmActions();
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState(ALL);
  const [search, setSearch] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDoc, setEditDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const loadFolders = useCallback(() => runService.listFolders().then(setFolders).catch(() => {}), []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { documents } = await runService.listDocuments({ folder_id: folder === ALL ? undefined : folder, search: search || undefined });
      setDocs(documents);
    } catch { toast.error("Could not load documents"); }
    finally { setLoading(false); }
  }, [folder, search]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => { loadFolders(); load(); };
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [loadFolders, load]);

  const download = async (d) => {
    setDownloading(d.id);
    try {
      const blob = await runService.downloadDocument(d.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.message || "Could not download"); }
    finally { setDownloading(null); }
  };

  const confirmDelete = async () => {
    try { await runService.deleteDocument(deleteDoc.id); toast.success("Document deleted"); setDeleteDoc(null); loadFolders(); load(); }
    catch (e) { toast.error(e.message || "Could not delete"); }
  };

  const relPath = (r) => (!r ? null : r.type === "transaction" ? `/transactions/${r.id}` : r.contact_id ? `/crm/${r.contact_id}` : null);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="documents-page">
        <PageHeader eyebrow="AgentHQ · Run" title="Documents"
          description="Keep important files connected to your business — stored privately and linked to clients, leads and transactions."
          actions={<Button onClick={() => openUploadDocument()} data-testid="doc-upload-btn"><Upload className="h-4 w-4" /> Upload Document</Button>} />

        <div className="flex flex-wrap items-center gap-2" data-testid="doc-folders">
          <button onClick={() => setFolder(ALL)} data-testid="doc-folder-all"
            className={`rounded-full border px-3 py-1.5 text-sm ${folder === ALL ? "border-brand-navy bg-brand-navy text-white" : "border-border text-muted-foreground hover:text-foreground"}`}>All files</button>
          {folders.map((f) => (
            <button key={f.id} onClick={() => setFolder(f.id)} data-testid={`doc-folder-${f.id}`}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${folder === f.id ? "border-brand-navy bg-brand-navy text-white" : "border-border text-muted-foreground hover:text-foreground"}`}>
              <Folder className="h-3.5 w-3.5" /> {f.name}{f.count ? ` (${f.count})` : ""}
            </button>
          ))}
        </div>

        <div className="flex min-w-[200px] items-center gap-2 rounded-lg border border-border px-3 sm:max-w-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search files…" className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="doc-search" />
        </div>

        {loading ? <div className="py-10"><LoadingState label="Loading documents" /></div>
          : docs.length === 0 ? (
            <EmptyState title="Keep important files connected to your business."
              description="Upload a document and link it to a client, lead, or transaction."
              action={<Button className="mt-5" onClick={() => openUploadDocument()} data-testid="doc-empty-upload"><Upload className="h-4 w-4" /> Upload Document</Button>} />
          ) : (
            <div className="space-y-2" data-testid="doc-list">
              {docs.map((d) => {
                const Icon = fileIcon(d.file_type);
                const rp = relPath(d.related);
                return (
                  <div key={d.id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3" data-testid={`doc-item-${d.id}`}>
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-secondary text-accent"><Icon className="h-5 w-5" /></div>
                    <div className="min-w-0 flex-1">
                      <button onClick={() => download(d)} className="truncate text-left text-sm font-medium text-foreground hover:text-accent" data-testid={`doc-name-${d.id}`}>{d.file_name}</button>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                        <span className="uppercase">{d.file_type}</span>
                        <span>{fmtSize(d.file_size)}</span>
                        <span>{fmtDate(d.created_at)}</span>
                        {d.folder && <span className="flex items-center gap-1"><Folder className="h-3 w-3" /> {d.folder.name}</span>}
                        {d.related?.label && (
                          <button className="flex items-center gap-1 hover:text-accent disabled:hover:text-muted-foreground" disabled={!rp} onClick={() => rp && navigate(rp)} data-testid={`doc-related-${d.id}`}>
                            <Link2 className="h-3 w-3" /> {d.related.label}
                          </button>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => download(d)} disabled={downloading === d.id} data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="rounded-md border border-border p-1.5 hover:bg-secondary" data-testid={`doc-actions-${d.id}`} aria-label="Document actions"><MoreHorizontal className="h-4 w-4" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => setEditDoc(d)} data-testid={`doc-edit-${d.id}`}><Pencil className="h-4 w-4" /> Rename / Move / Link</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setDeleteDoc(d)} className="text-destructive" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      <DocumentEditDialog open={!!editDoc} onOpenChange={(v) => !v && setEditDoc(null)} document={editDoc} folders={folders} onSaved={() => { setEditDoc(null); loadFolders(); load(); }} />

      <AlertDialog open={!!deleteDoc} onOpenChange={(v) => !v && setDeleteDoc(null)}>
        <AlertDialogContent data-testid="doc-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>“{deleteDoc?.file_name}” will be removed from your workspace. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="doc-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="doc-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
